import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import { useBranchNav } from '../../hooks/branchNav.hook';
import AuthContext from '../../context/auth.context';
import {
  canDeleteEmployee,
  canEditEmployee,
  operatorTypeLabel,
  readOperatorId,
  readOperatorTypeId,
} from '../../types/OperatorTypes';
import '../dashboard/Dashboard.css';
import '../shipment/ShipmentsPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import IconButton from '../dashboard/components/product_item_components/IconButton';
import { EditIcon, TrashIcon } from '../dashboard/components/product_item_components/icons';
import DeleteProductModal from '../dashboard/components/product_item_components/DeleteProductModal';

function employeesCountLabel(count) {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return 'сотрудников';
  if (n1 === 1) return 'сотрудник';
  if (n1 >= 2 && n1 <= 4) return 'сотрудника';
  return 'сотрудников';
}

function EmployeesPage() {
  const navigate = useNavigate();
  const { branchHomePath } = useBranchNav();
  const auth = React.useContext(AuthContext);
  const operatorId = readOperatorId(auth?.token);
  const operatorTypeId = readOperatorTypeId(auth?.token);
  const { loading: httpLoading, requestWithMeta } = useHttp();

  const [employees, setEmployees] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [employeeToDelete, setEmployeeToDelete] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState(null);

  const fetchEmployees = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await requestWithMeta('/operator/employees', 'GET');
      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить сотрудников');
        setEmployees([]);
        return;
      }
      const list = Array.isArray(res?.data?.employees) ? res.data.employees : [];
      setEmployees(list);
    } catch (_) {
      setError('Ошибка загрузки');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  React.useEffect(() => {
    if (!employeeToDelete) setDeleteError(null);
  }, [employeeToDelete]);

  const handleEdit = (employee) => {
    const id = employee?.id;
    if (id == null) return;
    navigate(`/employees/${encodeURIComponent(id)}/edit`, { state: { employee } });
  };

  const handleConfirmDelete = async () => {
    const employee = employeeToDelete;
    const id = employee?.id;
    if (id == null) return;

    setDeleteError(null);
    const res = await requestWithMeta(
      `/operator/employees/${encodeURIComponent(id)}`,
      'DELETE'
    );
    if (!res?.ok) {
      setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить сотрудника');
      return;
    }

    setEmployees((prev) => prev.filter((item) => String(item?.id) !== String(id)));
    setEmployeeToDelete(null);
  };

  const deleteName =
    employeeToDelete?.name ??
    (employeeToDelete ? `Сотрудник #${employeeToDelete?.id}` : '');

  return (
    <div className="shipments-page-root">
      <CreateBusinessHeader
        title="Сотрудники"
        subtitle="Вход через браузер по логину и паролю"
        onBack={() => navigate(branchHomePath)}
      />

      <main className="shipments-page-content">
        {loading ? (
          <div className="shipments-page-loading" aria-busy="true">
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
          </div>
        ) : error ? (
          <div className="shipments-page-error">{error}</div>
        ) : (
          <>
            <div className="main-page-sectionHeader shipments-page-sectionHeader">
              <div className="main-page-sectionSub">
                <span>
                  {employees.length
                    ? `${employees.length} ${employeesCountLabel(employees.length)}`
                    : 'Нет сотрудников'}
                </span>
              </div>
            </div>

            {employees.length === 0 ? (
              <div className="shipments-page-empty">Нет сотрудников</div>
            ) : (
              <ul className="shipments-page-list" aria-label="Список сотрудников">
                {employees.map((employee) => {
                  const id = employee?.id;
                  const name = employee?.name ?? `Сотрудник #${id}`;
                  const editable = canEditEmployee(employee, operatorId, operatorTypeId);
                  const deletable = canDeleteEmployee(employee, operatorId, operatorTypeId);
                  const meta = `${operatorTypeLabel(employee?.typeId)} · ${employee?.username ?? ''}`;

                  return (
                    <li key={id} className="shipments-page-item">
                      <div className="shipments-page-itemRow">
                        {editable ? (
                          <button
                            type="button"
                            className="shipments-page-itemMain"
                            onClick={() => handleEdit(employee)}
                            aria-label={`Редактировать «${name}»`}
                          >
                            <span className="shipments-page-itemName">{name}</span>
                            <span className="shipments-page-itemPrice">{meta}</span>
                          </button>
                        ) : (
                          <div className="shipments-page-itemMain shipments-page-itemMain--static">
                            <span className="shipments-page-itemName">{name}</span>
                            <span className="shipments-page-itemPrice">{meta}</span>
                          </div>
                        )}
                        {editable ? (
                          <div className="shipments-page-itemActions main-page-productActions">
                            <IconButton
                              onClick={() => handleEdit(employee)}
                              label={`Редактировать «${name}»`}
                              title="Редактировать"
                              disabled={httpLoading}
                            >
                              <EditIcon />
                            </IconButton>
                            {deletable ? (
                              <IconButton
                                onClick={() => setEmployeeToDelete(employee)}
                                label={`Удалить «${name}»`}
                                title="Удалить"
                                disabled={httpLoading}
                                className="main-page-iconButton"
                              >
                                <TrashIcon />
                              </IconButton>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>

      <footer className="shipments-page-footerRow">
        <button
          type="button"
          className="shipments-page-footerButton"
          onClick={() => navigate('/employees/create')}
          disabled={loading || Boolean(error)}
        >
          + Добавить сотрудника
        </button>
      </footer>

      <DeleteProductModal
        isOpen={!!employeeToDelete}
        onClose={() => setEmployeeToDelete(null)}
        title="Удалить сотрудника"
        productName={deleteName}
        deleteError={deleteError}
        loading={httpLoading}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default EmployeesPage;
